import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { EmailApi } from '../../services/ApiService';
import { useToast } from '../../contexts/ToastContext';

export default function EmailEdit() {
  const { id } = useParams();
  const isCreate = !id;
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [templateKey, setTemplateKey] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [templateKeyError, setTemplateKeyError] = useState(null);

  const loadEmail = useCallback(async () => {
    if (isCreate) return;
    setLoading(true); setError(null);
    try {
      const res = await EmailApi.getEmail(id);
      if (!res.ok) throw new Error(`Failed to load email: ${res.status}`);
      const data = await res.json();
  setTemplateKey(data.templateKey || '');
  setSubject(data.subject || '');
  setBodyHtml(data.bodyHtml || '');
  setBodyText(data.bodyText || '');
  setDescription(data.description || '');
  setIsActive(typeof data.isActive === 'boolean' ? data.isActive : true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id, isCreate]);

  useEffect(() => { loadEmail(); }, [loadEmail]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null); setSaveError(null); setTemplateKeyError(null);
    try {
      const trimmedKey = templateKey.trim();
      if (!/^([a-z0-9]+(-[a-z0-9]+)*)$/.test(trimmedKey)) {
        setTemplateKeyError('Use lowercase letters, numbers, hyphens only (no spaces).');
        throw new Error('Invalid template key format');
      }
      const payload = { templateKey: trimmedKey, subject: subject.trim(), bodyHtml, bodyText, description: description.trim(), isActive };
      const res = isCreate ? await EmailApi.createEmail(payload) : await EmailApi.updateEmail(id, payload);
      if (!res.ok) {
        if (res.status === 409) {
          setTemplateKeyError('Template key already exists. Choose a different key.');
          throw new Error('Duplicate template key');
        }
        const text = await res.text();
        throw new Error(text || `Save failed: ${res.status}`);
      }
      addToast({ intent: 'success', message: isCreate ? 'Template created.' : 'Template updated.' });
      if (isCreate) {
        const created = await res.json();
        navigate(`/settings/emails/${created.id || created.emailId}`);
      }
    } catch (e2) {
      setError(e2.message);
      setSaveError(e2.message);
      addToast({ intent: 'danger', message: `Error: ${e2.message}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading-state"><div className="loading-spinner" />Loading...</div>;
  }

  return (
    <div style={{padding:'1.25rem',fontSize:'var(--font-base,14px)'}} className="email-edit-page">
      <h3 style={{margin:'0 0 1.25rem',fontSize:'var(--font-lg,1.4rem)',fontWeight:600}}>{isCreate ? 'Create Email Template' : `Edit Template #${id}`}</h3>
      {error && <div className="error-message" role="alert">{error}</div>}
      {saveError && (
        <div style={{background:'var(--color-danger-bg,#fee)',color:'var(--color-danger-text,#900)',padding:'8px 12px',borderRadius:'4px',fontSize:'.75rem',marginBottom:'12px'}} role="alert">
          {saveError}
        </div>
      )}
      <div style={{background:'var(--panel-bg,#f9fafb)',border:'1px solid var(--panel-border,#e2e8f0)',borderRadius:'6px',padding:'14px 16px',marginBottom:'20px',display:'flex',flexDirection:'column',gap:'10px',fontSize:'var(--font-sm,0.85rem)'}}>
        <strong style={{fontSize:'var(--font-sm,0.85rem)'}}>Template Guidance</strong>
        <ul style={{margin:0,paddingLeft:'20px',fontSize:'var(--font-xs,0.75rem)',lineHeight:'1.3',display:'flex',flexDirection:'column',gap:'6px'}}>
          <li><code style={{fontSize:'.65rem'}}>{'{{placeholder}}'}</code> syntax works in Subject, HTML Body, and Text Body. Case-sensitive.</li>
          <li>Provide both <strong>HTML</strong> and <strong>Text</strong> bodies for better deliverability & accessibility.</li>
          <li>Optional placeholders not supplied during send remain unchanged; use fallback wording when possible.</li>
          <li>Avoid overlapping placeholder names (e.g. <code>{'{{plan}}'}</code> and <code>{'{{planName}}'}</code>) to prevent partial replacement collisions.</li>
          <li>Keep plain text concise and include raw URLs present in the HTML for phishing consistency.</li>
          <li>Dynamic values are inserted verbatim; sanitize if sourcing from untrusted input.</li>
          <li>Inactive templates cannot be sent; toggle Active to enable.</li>
        </ul>
      </div>
  <form onSubmit={handleSave} style={{display:'flex',flexDirection:'column',gap:'16px',maxWidth:'900px',fontSize:'var(--font-sm,0.9rem)'}}>
  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))',gap:'20px'}}>
          <label style={{display:'flex',flexDirection:'column',gap:'6px',fontSize:'var(--font-xs,0.75rem)',fontWeight:600}}>Template Key
            <input
              type="text"
              value={templateKey}
              onChange={e=>setTemplateKey(e.target.value)}
              required
              placeholder="welcome, password-reset, etc"
              style={{padding:'8px 10px'}}
            />
            <span style={{fontSize:'var(--font-xs,0.7rem)',color: templateKeyError ? 'var(--color-danger-text,#900)' : 'var(--text-subtle,#555)'}}>
              {templateKeyError ? templateKeyError : 'Lowercase slug; use hyphens. Example: password-reset-v2'}
            </span>
          </label>
          <label style={{display:'flex',flexDirection:'column',gap:'6px',fontSize:'var(--font-xs,0.75rem)',fontWeight:600}}>Subject
            <input
              type="text"
              value={subject}
              onChange={e=>setSubject(e.target.value)}
              required
              placeholder="Welcome {{firstName}}"
              style={{padding:'8px 10px'}}
            />
            <span style={{fontSize:'var(--font-xs,0.7rem)',color:'var(--text-subtle,#555)'}}>Use tokens like {'{{firstName}}'}, {'{{lastName}}'}, {'{{organizationName}}'}.</span>
          </label>
          <label style={{display:'flex',flexDirection:'column',gap:'6px',fontSize:'var(--font-xs,0.75rem)',fontWeight:600}}>Active?
            <div className="themed-checkbox-wrapper" style={{display:'flex',alignItems:'center',gap:'6px'}}>
              <input type="checkbox" checked={isActive} onChange={e=>setIsActive(e.target.checked)} className="themed-checkbox" />
              <span style={{fontSize:'var(--font-xs,0.7rem)'}}>Active templates are eligible for sending.</span>
            </div>
          </label>
        </div>
        <label style={{display:'flex',flexDirection:'column',gap:'6px',fontSize:'var(--font-xs,0.75rem)',fontWeight:600}}>Description
          <input
            type="text"
            value={description}
            onChange={e=>setDescription(e.target.value)}
            placeholder="Internal description"
            style={{padding:'8px 10px'}}
          />
        </label>
  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'20px'}}>
          <label style={{display:'flex',flexDirection:'column',gap:'6px',fontSize:'var(--font-xs,0.75rem)',fontWeight:600}}>HTML Body
            <textarea
              value={bodyHtml}
              onChange={e=>setBodyHtml(e.target.value)}
              required
              placeholder='<html><body><h1>Hi {{firstName}}</h1></body></html>'
              rows={14}
              style={{padding:'8px 10px',fontFamily:'monospace'}}
            />
            <span style={{fontSize:'var(--font-xs,0.7rem)',color:'var(--text-subtle,#555)'}}>Insert tokens with double curly braces, e.g. {'{{firstName}}'}. Avoid inline scripts; basic HTML only.</span>
          </label>
          <label style={{display:'flex',flexDirection:'column',gap:'6px',fontSize:'var(--font-xs,0.75rem)',fontWeight:600}}>Text Body
            <textarea
              value={bodyText}
              onChange={e=>setBodyText(e.target.value)}
              required
              placeholder='Hi {{firstName}}'
              rows={14}
              style={{padding:'8px 10px',fontFamily:'monospace'}}
            />
            <span style={{fontSize:'var(--font-xs,0.7rem)',color:'var(--text-subtle,#555)'}}>Plain text fallback for clients that do not render HTML.</span>
          </label>
        </div>
  <div style={{display:'flex',gap:'10px',marginTop:'12px',flexWrap:'wrap',fontSize:'var(--font-sm,0.9rem)'}}>
          <button className="btn-primary btn-sm" type="submit" disabled={saving}>{saving ? 'Saving...' : (isCreate ? 'Create' : 'Save')}</button>
          <button type="button" className="btn-secondary btn-sm" onClick={()=>navigate('/settings/emails')}>Back</button>
        </div>
      </form>
    </div>
  );
}
